<template>
    <div class="shadow-sm sm:overflow-hidden sm:rounded-md">
        <div class="bg-white px-4 py-4 sm:p-6">
            <div class="flex items-center justify-between gap-2">
                <h3 class="text-base font-semibold text-gray-900">{{ pricesBlockTitle }}</h3>
                <button
                    type="button"
                    class="inline-flex items-center gap-1 rounded-md border border-royal-blue-600 text-royal-blue-600 px-2 py-1.5 text-xs hover:bg-royal-blue-600 cursor-pointer hover:text-white"
                    @click="beginAdd"
                >
                    <PlusIcon class="size-4" />
                    <span>{{ $t('profile.prices_add_button') }}</span>
                </button>
            </div>
            <div>
                <p class="mt-1 text-sm text-gray-500">{{ $t('profile.prices_tip') }}</p>
            </div>
        </div>
    </div>

    <div class="py-4">
        <draggable
            v-model="localItems"
            item-key="id"
            :group="{ name: 'profile-prices', pull: true, put: true }"
            @end="onDragEnd"
            tag="ul"
            class="grid grid-cols-1 sm:grid-cols-2 gap-4"
            handle=".handle"
        >
            <template #item="{ element }">
                <li class="relative bg-white shadow-sm rounded-lg flex flex-col p-3">
                    <div class="flex items-start justify-between">
                        <div class="w-full">
                            <div class="grid grid-cols-6 gap-2">
                                <div class="col-span-6">
                                    <input v-model="element.title" type="text" :placeholder="$t('profile.prices_title_placeholder')" class="block w-full rounded-md bg-white px-3 py-2 text-sm text-gray-900 outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:-outline-offset-2 focus:outline-royal-blue-600" />
                                </div>
                                <div class="col-span-6 flex gap-2">
                                    <input v-model.number="element.price" type="number" step="0.01" min="0" :placeholder="$t('profile.prices_price')" class="block w-full rounded-md bg-white px-3 py-2 text-sm text-gray-900 outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:-outline-offset-2 focus:outline-royal-blue-600" />
                                    <select v-model="element.currency" class="rounded-md bg-white px-3 py-2 text-sm text-gray-900 outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:-outline-offset-2 focus:outline-royal-blue-600">
                                        <option value="USD">$</option>
                                        <option value="EUR">€</option>
                                        <option value="UAH">₴</option>
                                        <option value="PLN">zł</option>
                                    </select>
                                </div>
                                <div class="col-span-6">
                                    <div>
                                        <div class="cursor-pointer p-4 flex bg-white border border-gray-200 rounded-lg" @click="triggerFile(element.id)">
                                            <div class="flex items-center gap-4 w-full">
                                                <template v-if="!element.cover_url">
                                                    <svg class="shrink-0 size-16" width="71" height="51" viewBox="0 0 71 51" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                      <path d="M6.55172 8.74547L17.7131 6.88524V40.7377L12.8018 41.7717C9.51306 42.464 6.29705 40.3203 5.67081 37.0184L1.64319 15.7818C1.01599 12.4748 3.23148 9.29884 6.55172 8.74547Z" stroke="currentColor" stroke-width="2" class="stroke-gray-700"></path>
                                                      <path d="M64.4483 8.74547L53.2869 6.88524V40.7377L58.1982 41.7717C61.4869 42.464 64.703 40.3203 65.3292 37.0184L69.3568 15.7818C69.984 12.4748 67.7685 9.29884 64.4483 8.74547Z" stroke="currentColor" stroke-width="2" class="stroke-gray-700"></path>
                                                      <g filter="url(#filter1)">
                                                        <rect x="17.5656" y="1" width="35.8689" height="42.7541" rx="5" stroke="currentColor" stroke-width="2" class="stroke-gray-700" shape-rendering="crispEdges"></rect>
                                                      </g>
                                                      <path d="M39.4826 33.0893C40.2331 33.9529 41.5385 34.0028 42.3537 33.2426L42.5099 33.0796L47.7453 26.976L53.4347 33.0981V38.7544C53.4346 41.5156 51.1959 43.7542 48.4347 43.7544H22.5656C19.8043 43.7544 17.5657 41.5157 17.5656 38.7544V35.2934L29.9728 22.145L39.4826 33.0893Z" fill="currentColor" stroke="currentColor" stroke-width="2" class="fill-gray-100 stroke-gray-700"></path>
                                                      <circle cx="40.0902" cy="14.3443" r="4.16393" fill="currentColor" stroke="currentColor" stroke-width="2" class="fill-gray-100 stroke-gray-700"></circle>
                                                      <defs>
                                                        <filter id="filter1" x="13.5656" y="0" width="43.8689" height="50.7541" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
                                                          <feFlood flood-opacity="0" result="BackgroundImageFix"></feFlood>
                                                          <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"></feColorMatrix>
                                                          <feOffset dy="3"></feOffset>
                                                          <feGaussianBlur stdDeviation="1.5"></feGaussianBlur>
                                                          <feComposite in2="hardAlpha" operator="out"></feComposite>
                                                          <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.12 0"></feColorMatrix>
                                                          <feBlend mode="normal" in2="BackgroundImageFix" result="effect1"></feBlend>
                                                          <feBlend mode="normal" in="SourceGraphic" in2="effect1" result="shape"></feBlend>
                                                        </filter>
                                                      </defs>
                                                    </svg>
                                                </template>
                                                <img v-else :src="element.cover_url" alt="" class="shrink-0 size-16 rounded-md object-cover" />

                                                <div class="grow">
                                                  <div class="inline-flex flex-wrap gap-1 text-sm/6 text-gray-600">
                                                    <span class="text-gray-800">
                                                      {{ $t('profile.prices_cover_drop_or') }}
                                                    </span>
                                                    <span class="bg-white text-royal-blue-700 hover:text-royal-blue-800 rounded-lg decoration-2 hover:underline focus-within:outline-hidden focus-within:ring-2 focus-within:ring-royal-blue-800 focus-within:ring-offset-2">{{ $t('profile.prices_cover_browse') }}</span>
                                                  </div>

                                                  <p class="text-xs text-gray-400">
                                                    {{ $t('profile.prices_cover_size') }}
                                                  </p>
                                                </div>

                                                <div class="flex items-center gap-2 justify-end">
                                                    <button v-if="element.cover_url" type="button" class="text-gray-500 hover:text-red-600" @click.stop="deleteCover(element)">
                                                        <svg class="shrink-0 size-4" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 6h18"></path><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path><line x1="10" x2="10" y1="11" y2="17"></line><line x1="14" x2="14" y1="11" y2="17"></line></svg>
                                                    </button>
                                                </div>
                                            </div>
                                        </div>

                                        <div class="mt-2" v-if="uploadProgress[element.id] != null">
                                            <div class="p-3 bg-white border border-solid border-gray-300 rounded-lg">
                                                <div class="mb-1 flex justify-between items-center">
                                                    <div class="text-sm font-medium text-gray-800">{{ $t('profile.prices_cover_label') }}</div>
                                                    <div class="text-sm text-gray-800">{{ uploadProgress[element.id] }}%</div>
                                                </div>
                                                <div class="flex items-center gap-x-3 whitespace-nowrap">
                                                    <div class="flex w-full h-2 bg-gray-200 rounded-full overflow-hidden" role="progressbar" aria-valuemin="0" aria-valuemax="100">
                                                        <div class="flex flex-col justify-center rounded-full overflow-hidden bg-royal-blue-700 text-xs text-white text-center whitespace-nowrap transition-all duration-500" :style="{ width: uploadProgress[element.id] + '%' }"></div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <input :ref="`fileInput_${element.id}`" type="file" class="hidden" accept="image/*" @change="String(element.id).startsWith('tmp-') ? onCoverSelectedTemp($event, element) : onCoverSelected($event, element)" />
                                    </div>
                                </div>
                                <div class="col-span-6">
                                    <label class="block mb-2 font-medium text-sm text-gray-800">{{ $t('profile.description') }}</label>
                                    <UserDescriptionEditor v-model="element.description" :placeholder="$t('profile.prices_description_placeholder')" />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="pt-3 mt-auto flex items-center gap-2 justify-end">
                        <div class="flex items-center gap-2 mr-auto">
                            <div class="cursor-move text-gray-400 hover:text-gray-600 p-1 handle">
                                <svg class="size-5" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                    <circle cx="8" cy="6" r="1"/>
                                    <circle cx="8" cy="12" r="1"/>
                                    <circle cx="8" cy="18" r="1"/>
                                    <circle cx="16" cy="6" r="1"/>
                                    <circle cx="16" cy="12" r="1"/>
                                    <circle cx="16" cy="18" r="1"/>
                                </svg>
                            </div>
                        </div>
                        <button class="text-white bg-red-600 px-4 py-2 rounded-md hover:bg-red-700 cursor-pointer" @click="remove(element)">
                            <TrashIcon class="size-4" />
                        </button>
                        <button class="inline-flex justify-center rounded-md bg-blue-700 px-4 py-2 text-xs font-semibold text-white shadow-xs hover:bg-blue-600 cursor-pointer" @click="save(element)">{{ $t('profile.save') }}</button>
                    </div>
                </li>
            </template>
        </draggable>
        <!-- Empty State -->
        <div v-if="localItems.length === 0" class="p-5 min-h-96 flex flex-col justify-center items-center text-center">
            <svg class="w-48 mx-auto mb-4" width="178" height="114" viewBox="0 0 178 114" fill="none" xmlns="http://www.w3.org/2000/svg">
                <!-- three vertical cards -->
                <rect x="18" y="12" width="42" height="96" rx="8" fill="currentColor" class="fill-white"/>
                <rect x="18" y="12" width="42" height="96" rx="8" stroke="currentColor" class="stroke-gray-100"/>
                <rect x="26" y="20" width="26" height="26" rx="4" fill="currentColor" class="fill-gray-100"/>
                <rect x="26" y="50" width="26" height="6" rx="3" fill="currentColor" class="fill-gray-100"/>
                <rect x="26" y="62" width="26" height="6" rx="3" fill="currentColor" class="fill-gray-100"/>

                <rect x="68" y="6" width="42" height="108" rx="8" fill="currentColor" class="fill-white"/>
                <rect x="68" y="6" width="42" height="108" rx="8" stroke="currentColor" class="stroke-gray-100"/>
                <rect x="76" y="14" width="26" height="26" rx="4" fill="currentColor" class="fill-gray-200"/>
                <rect x="76" y="44" width="26" height="6" rx="3" fill="currentColor" class="fill-gray-200"/>
                <rect x="76" y="56" width="26" height="6" rx="3" fill="currentColor" class="fill-gray-200"/>
                <rect x="76" y="68" width="26" height="6" rx="3" fill="currentColor" class="fill-gray-200"/>

                <rect x="118" y="12" width="42" height="96" rx="8" fill="currentColor" class="fill-white"/>
                <rect x="118" y="12" width="42" height="96" rx="8" stroke="currentColor" class="stroke-gray-100"/>
                <rect x="126" y="20" width="26" height="26" rx="4" fill="currentColor" class="fill-gray-100"/>
                <rect x="126" y="50" width="26" height="6" rx="3" fill="currentColor" class="fill-gray-100"/>
                <rect x="126" y="62" width="26" height="6" rx="3" fill="currentColor" class="fill-gray-100"/>
            </svg>

            <div class="max-w-sm mx-auto">
                <p class="mt-2 font-medium text-gray-800">{{ $t('profile.prices_empty') }}</p>
                <p class="mb-5 text-sm text-gray-500">{{ $t('profile.prices_empty_description') }}</p>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import draggable from 'vuedraggable';
import { mapActions, mapGetters } from 'vuex';
import { PlusIcon, TrashIcon } from '@heroicons/vue/16/solid';
import { UserProfilePrice } from '@/types/PriceType';
import UserDescriptionEditor from '@/components/Users/Profile/UserDescriptionEditor.vue';

export default defineComponent({
    name: 'PricesSection',
    components: {
        draggable,
        PlusIcon,
        TrashIcon,
        UserDescriptionEditor,
    },
    data() {
        return {
            localItems: [] as UserProfilePrice[],
            uploadProgress: {} as Record<string, number>,
        };
    },
    computed: {
        ...mapGetters({
            prices: 'profilePrices/prices',
            profile: 'profile/profile',
        }),
        pricesBlockTitle(): string {
            const custom = this.profile?.prices_block_title;
            if (custom && String(custom).trim() !== '') {
                return String(custom).trim();
            }
            return this.$t('profile.prices_title') as string;
        },
    },
    watch: {
        prices: {
            deep: true,
            immediate: true,
            handler(val: UserProfilePrice[]) {
                this.localItems = Array.isArray(val) ? val.map((i) => ({ ...i })) : [];
            },
        },
    },
    mounted() {
        this.loadPrices();
    },
    methods: {
        ...mapActions({
            loadPrices: 'profilePrices/loadPrices',
            createPriceAction: 'profilePrices/createPrice',
            updatePriceAction: 'profilePrices/updatePrice',
            deletePriceAction: 'profilePrices/deletePrice',
            sortPricesAction: 'profilePrices/sortPrices',
            uploadCoverAction: 'profilePrices/uploadCover',
            deleteCoverAction: 'profilePrices/deleteCover',
            successAlert: 'alerts/success',
            errorAlert: 'alerts/error',
        }),
        beginAdd() {
            this.localItems.push({
                id: `tmp-${Date.now()}`,
                title: '',
                description: '',
                price: null,
                currency: 'UAH',
                order_number: ((this.localItems.length ? this.localItems[this.localItems.length - 1].order_number : -1) + 1),
                cover_url: null, // Додаємо поле для обгортки
            });
        },
        async save(item: UserProfilePrice) {
            const payload = { title: item.title, description: item.description, price: item.price, currency: item.currency };
            const isTemp = String(item.id).startsWith('tmp-');
            
            try {
                if (isTemp) {
                    // Якщо це тимчасовий блок з обгорткою, зберігаємо його з обгорткою
                    if (item.cover_url && item.cover_url.startsWith('data:')) {
                        // Конвертуємо data URL назад у файл
                        const response = await fetch(item.cover_url);
                        const blob = await response.blob();
                        const file = new File([blob], 'cover.jpg', { type: 'image/jpeg' });
                        
                        await this.saveWithCover(item, file);
                        return;
                    }
                    
                    // Якщо обгортки немає, зберігаємо звичайно
                    await this.createPriceAction(payload as any);
                } else {
                    await this.updatePriceAction({ id: item.id, data: payload as any });
                }
                this.successAlert({ message: this.$t('profile.success_saved'), timeout: 5 });
                await this.loadPrices();
            } catch (e: any) {
                this.errorAlert({ message: e?.data?.message || this.$t('profile.portfolio_unknown_error'), timeout: 10 });
            }
        },
        async remove(item: UserProfilePrice) {
            const isTemp = String(item.id).startsWith('tmp-');
            
            if (isTemp) {
                // Якщо це тимчасовий блок з обгорткою, очищаємо URL
                if (item.cover_url && item.cover_url.startsWith('data:')) {
                    URL.revokeObjectURL(item.cover_url);
                }
                this.localItems = this.localItems.filter((i) => i.id !== item.id);
                return;
            }
            
            try {
                await this.deletePriceAction({ id: item.id });
                await this.loadPrices();
            } catch (e: any) {
                this.errorAlert({ message: e?.data?.message || this.$t('profile.deleting_error'), timeout: 10 });
            }
        },
        onDragEnd() {
            this.sortPricesAction({ items: this.localItems });
        },
        async onCoverSelected(e: Event, item: UserProfilePrice) {
            const input = e.target as HTMLInputElement;
            const file = input.files && input.files[0] ? input.files[0] : null;
            if (!file) return;
            
            const isTemp = String(item.id).startsWith('tmp-');
            
            try {
                // Якщо це тимчасовий блок, спочатку зберігаємо його
                if (isTemp) {
                    // Перевіряємо, чи є мінімальні дані для збереження
                    if (!item.title || item.title.trim() === '') {
                        // Якщо назва порожня, встановлюємо значення за замовчуванням
                        item.title = this.$t('profile.prices_title_placeholder') || 'Нова послуга';
                    }
                    
                    // Зберігаємо блок
                    await this.saveWithCover(item, file);
                    return;
                }
                
                // Якщо блок вже збережений, завантажуємо обгортку
                this.uploadProgress = { ...this.uploadProgress, [item.id]: 0 };
                await this.uploadCoverAction({ id: item.id, file, onProgress: (p: number) => {
                    this.uploadProgress = { ...this.uploadProgress, [item.id]: p };
                }});
                await this.loadPrices();
                this.successAlert({ message: this.$t('profile.success_saved'), timeout: 5 });
            } catch (e: any) {
                this.errorAlert({ message: e?.data?.message || this.$t('profile.cover_error'), timeout: 10 });
            } finally {
                // Очищаємо input файлу
                input.value = '';
                setTimeout(() => {
                    const rest = { ...this.uploadProgress } as Record<string, number>;
                    delete rest[item.id];
                    this.uploadProgress = rest;
                }, 600);
            }
        },
        async deleteCover(item: UserProfilePrice) {
            const isTemp = String(item.id).startsWith('tmp-');
            
            // Якщо це тимчасовий блок, просто очищаємо обгортку локально
            if (isTemp) {
                const index = this.localItems.findIndex(i => i.id === item.id);
                if (index !== -1) {
                    this.localItems[index].cover_url = null;
                }
                return;
            }
            
            // Якщо блок збережений, видаляємо обгортку з сервера
            try {
                await this.deleteCoverAction({ id: item.id });
                await this.loadPrices();
                this.successAlert({ message: this.$t('profile.success_saved'), timeout: 5 });
            } catch (e: any) {
                this.errorAlert({ message: e?.data?.message || this.$t('profile.cover_error'), timeout: 10 });
            }
        },
        triggerFile(elementId: string | number) {
            const inputRef = `fileInput_${elementId}`;
            const input = this.$refs[inputRef] as HTMLInputElement | undefined;
            if (input) {
                input.click();
            } else {
                console.warn(`Input not found for element ID: ${elementId}`);
            }
        },
        // Альтернативний метод для тимчасових блоків - зберігає обгортку локально
        onCoverSelectedTemp(e: Event, item: UserProfilePrice) {
            const input = e.target as HTMLInputElement;
            const file = input.files && input.files[0] ? input.files[0] : null;
            if (!file) return;
            
            // Показуємо індикатор завантаження для тимчасового блоку
            this.uploadProgress = { ...this.uploadProgress, [item.id]: 0 };
            
            // Створюємо URL для попереднього перегляду
            const reader = new FileReader();
            reader.onload = (e) => {
                const result = e.target?.result as string;
                const index = this.localItems.findIndex(i => i.id === item.id);
                if (index !== -1) {
                    this.localItems[index].cover_url = result;
                }
                
                // Показуємо 100% завантаження
                this.uploadProgress = { ...this.uploadProgress, [item.id]: 100 };
                
                // Приховуємо індикатор через 1 секунду
                setTimeout(() => {
                    const rest = { ...this.uploadProgress } as Record<string, number>;
                    delete rest[item.id];
                    this.uploadProgress = rest;
                }, 1000);
            };
            reader.readAsDataURL(file);
            
            // Очищаємо input файлу
            input.value = '';
        },
        
        async saveWithCover(item: UserProfilePrice, file: File) {
            try {
                // Спочатку зберігаємо блок
                const payload = { 
                    title: item.title, 
                    description: item.description, 
                    price: item.price, 
                    currency: item.currency 
                };
                
                const savedItem = await this.createPriceAction(payload as any);
                
                // Оновлюємо локальний масив з новим ID
                const index = this.localItems.findIndex(i => i.id === item.id);
                if (index !== -1) {
                    this.localItems[index] = { ...savedItem };
                }
                
                // Тепер завантажуємо обгортку
                this.uploadProgress = { ...this.uploadProgress, [savedItem.id]: 0 };
                await this.uploadCoverAction({ 
                    id: savedItem.id, 
                    file, 
                    onProgress: (p: number) => {
                        this.uploadProgress = { ...this.uploadProgress, [savedItem.id]: p };
                    }
                });
                
                await this.loadPrices();
                this.successAlert({ message: this.$t('profile.success_saved'), timeout: 5 });
            } catch (e: any) {
                this.errorAlert({ message: e?.data?.message || this.$t('profile.cover_error'), timeout: 10 });
            }
        },
    },
});
</script>


